/**
 * Atlas Rides Panel Component
 * 
 * Left panel displaying rides from Atlas (RidesContext).
 * Allows users to view, search, and select rides for upload operations.
 * 
 * @module AtlasRidesPanel
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';

import { useTranslation } from 'react-i18next';
import { useRides, loadRidesFromMapLayers } from '../../../../context/RidesContext';
import { RideCard } from './RideCard';
import { RideSearchBar } from './RideSearchBar';
import { useDebounce } from '../../../../hooks/useDebounce';
import type { RideSchema } from '../../../../types/simulation';
import { useContext } from 'react';
import { AppContext } from '../../../../context/context';

import { Icon } from '@liftango/ui-icons';
/**
 * Props for AtlasRidesPanel component
 */
export interface AtlasRidesPanelProps {
  /** Selected ride IDs */
  selectedRideIds: Set<string>;
  /** Callback when selection changes */
  onSelectionChange: (selectedIds: Set<string>) => void;
  /** Callback to update ride count in parent header */
  onRideCountChange?: (count: number, filteredCount?: number) => void;
  /** Callback to register refresh function */
  onRefreshRegister?: (refreshFn: () => void) => void;
  /** Start date for filtering rides (YYYY-MM-DD) */
  startDate?: string;
  /** End date for filtering rides (YYYY-MM-DD) */
  endDate?: string;
  /** Callback when start date changes */
  onStartDateChange?: (date: string) => void;
  /** Callback when end date changes */
  onEndDateChange?: (date: string) => void;
  /** Selected layer name for filtering rides */
  selectedLayerName?: string | null;
  /** Callback when layer selection changes */
  onLayerChange?: (layerName: string | null) => void;
}

/**
 * AtlasRidesPanel Component
 * 
 * Displays rides from RidesContext with search and selection functionality.
 */
export const AtlasRidesPanel: React.FC<AtlasRidesPanelProps> = ({
  selectedRideIds,
  onSelectionChange,
  onRideCountChange,
  onRefreshRegister,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  selectedLayerName,
  onLayerChange,
}) => {
  const { t } = useTranslation('mapTransfer');
  const context = useContext(AppContext);
  const { rides, searchQuery, setSearchQuery, filteredRides, selectRide, deselectRide, selectAll, deselectAll, getAllRides, bulkAddRides, removeRide } = useRides();
  const [expandedRideId, setExpandedRideId] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [localSelectedLayer, setLocalSelectedLayer] = useState<string | null>(selectedLayerName || null);
  
  // Date state management
  const [localStartDate, setLocalStartDate] = useState(startDate || '');
  const [localEndDate, setLocalEndDate] = useState(endDate || '');
  const [dateError, setDateError] = useState<string | null>(null);
  
  // Debounce search query
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  
  // Sync local dates with props
  useEffect(() => {
    if (startDate !== undefined) setLocalStartDate(startDate);
    if (endDate !== undefined) setLocalEndDate(endDate);
  }, [startDate, endDate]);
  
  // Validate dates
  useEffect(() => {
    if (localStartDate && localEndDate) {
      if (new Date(localStartDate) > new Date(localEndDate)) {
        setDateError(t('rides.panel.dateErrorStartAfterEnd'));
      } else {
        setDateError(null);
      }
    } else {
      setDateError(null);
    }
  }, [localStartDate, localEndDate, t]);
  
  // Debounce date changes
  const debouncedStartDate = useDebounce(localStartDate, 500);
  const debouncedEndDate = useDebounce(localEndDate, 500);
  
  // Notify parent of date changes
  useEffect(() => {
    if (!dateError && debouncedStartDate && onStartDateChange && debouncedStartDate !== startDate) {
      onStartDateChange(debouncedStartDate);
    }
  }, [debouncedStartDate, startDate, onStartDateChange, dateError]);
  
  useEffect(() => {
    if (!dateError && debouncedEndDate && onEndDateChange && debouncedEndDate !== endDate) {
      onEndDateChange(debouncedEndDate);
    }
  }, [debouncedEndDate, endDate, onEndDateChange, dateError]);
  
  // Auto-sync on mount: load rides from map layers if RidesContext is empty
  useEffect(() => {
    // If RidesContext is empty, try to load rides from existing map layers
    if (rides.length === 0 && context?.global?.mapLayers) {
      const loadedRides = loadRidesFromMapLayers(context.global.mapLayers);
      if (loadedRides.length > 0) {
        bulkAddRides(loadedRides, 'merge');
      }
    }
  }, [getAllRides, rides.length, context, bulkAddRides]);
  
  // Real-time updates: panel automatically reacts to RidesContext changes
  // since it uses `rides` directly from context
  useEffect(() => {
    // This effect ensures the panel stays in sync with context updates
    // The `rides` dependency will trigger re-renders when context changes
  }, [rides]);

  // Manual refresh handler
  const handleRefresh = useCallback(() => {
    setIsRefreshing(true);
    
    // Try to load rides from map layers if available
    if (context?.global?.mapLayers) {
      const loadedRides = loadRidesFromMapLayers(context.global.mapLayers);
      if (loadedRides.length > 0) {
        bulkAddRides(loadedRides, 'merge');
      }
    }
    
    // Simulate a brief loading state for user feedback
    setTimeout(() => {
      setIsRefreshing(false);
    }, 300);
  }, [getAllRides, context, bulkAddRides]);
  
  // Register refresh function
  useEffect(() => {
    if (onRefreshRegister) {
      onRefreshRegister(handleRefresh);
    }
  }, [onRefreshRegister, handleRefresh]);

  // Handle selection change
  const handleRideSelect = (rideId: string, selected: boolean) => {
    if (selected) {
      selectRide(rideId);
      const newSelected = new Set(selectedRideIds);
      newSelected.add(rideId);
      onSelectionChange(newSelected);
    } else {
      deselectRide(rideId);
      const newSelected = new Set(selectedRideIds);
      newSelected.delete(rideId);
      onSelectionChange(newSelected);
    }
  };

  // Handle select all
  const handleSelectAll = () => {
    selectAll();
    const allIds = new Set(filteredRidesList.map(r => r.ride_id || r.external_reference_id || '').filter(Boolean));
    onSelectionChange(allIds);
  };

  // Handle deselect all
  const handleDeselectAll = () => {
    deselectAll();
    onSelectionChange(new Set());
  };

  // Get available ride layers from map
  const availableLayers = useMemo(() => {
    if (!context?.global?.mapLayers) return [];
    
    const rideLayers = context.global.mapLayers.filter(layer => {
      // Check if layer contains rides (has lines with ride_id)
      return layer.shapes.some(shape => {
        if (shape.type === 'line') {
          const line = shape as any;
          return !!(line.ride_id || (line.user_id && (line.numPassengers || line.wheelchair)));
        }
        return false;
      });
    });
    
    return rideLayers.map(layer => layer.layerName).filter(Boolean) as string[];
  }, [context?.global?.mapLayers]);

  // Map ride IDs to their layer names
  const rideToLayerMap = useMemo(() => {
    const map = new Map<string, string>();
    if (!context?.global?.mapLayers) return map;
    
    context.global.mapLayers.forEach(layer => {
      layer.shapes.forEach(shape => {
        if (shape.type === 'line') {
          const line = shape as any;
          const rideId = line.ride_id;
          if (rideId && layer.layerName) {
            map.set(rideId, layer.layerName);
          }
        }
      });
    });
    
    return map;
  }, [context?.global?.mapLayers]);

  // Sync local layer selection with props
  useEffect(() => {
    if (selectedLayerName !== undefined) {
      setLocalSelectedLayer(selectedLayerName);
    }
  }, [selectedLayerName]);

  // Auto-select layer if only one available and none selected
  useEffect(() => {
    if (availableLayers.length === 1 && !localSelectedLayer) {
      setLocalSelectedLayer(availableLayers[0]);
      if (onLayerChange) {
        onLayerChange(availableLayers[0]);
      }
    }
  }, [availableLayers, localSelectedLayer, onLayerChange]);

  // Filter rides by selected layer
  const layerFilteredRides = useMemo(() => {
    if (!localSelectedLayer) {
      return filteredRides;
    }
    
    return filteredRides.filter(ride => {
      const rideId = ride.ride_id || ride.external_reference_id || '';
      const rideLayer = rideToLayerMap.get(rideId);
      return rideLayer === localSelectedLayer;
    });
  }, [filteredRides, localSelectedLayer, rideToLayerMap]);

  // Filter rides based on debounced search query (now uses layerFilteredRides)
  const filteredRidesList = useMemo(() => {
    if (!debouncedSearchQuery.trim()) {
      return layerFilteredRides;
    }
    
    const query = debouncedSearchQuery.toLowerCase();
    return layerFilteredRides.filter(ride => {
      const fromAddress = ride.from_address?.toLowerCase() || '';
      const toAddress = ride.to_address?.toLowerCase() || '';
      const rideId = ride.ride_id?.toLowerCase() || ride.external_reference_id?.toLowerCase() || '';
      const userId = ride.user_id?.toLowerCase() || ride.external_user_reference_id?.toLowerCase() || '';
      
      return fromAddress.includes(query) ||
             toAddress.includes(query) ||
             rideId.includes(query) ||
             userId.includes(query);
    });
  }, [layerFilteredRides, debouncedSearchQuery]);

  // Update parent with ride counts
  useEffect(() => {
    if (onRideCountChange) {
      onRideCountChange(rides.length, filteredRidesList.length);
    }
  }, [rides.length, filteredRidesList.length, onRideCountChange]);

  // Listen for shape deletions from map layers
  useEffect(() => {
    if (!context?.global?.mapLayers) return;
    
    // Check for deleted rides by comparing current layers with previous state
    // This is a simple approach - in a more complex scenario, we'd track deletions more directly
    const currentRideIds = new Set<string>();
    context.global.mapLayers.forEach(layer => {
      layer.shapes.forEach(shape => {
        if (shape.type === 'line') {
          const line = shape as any;
          if (line.ride_id) {
            currentRideIds.add(line.ride_id);
          }
        }
      });
    });
    
    // Find rides in context that are no longer in layers
    rides.forEach(ride => {
      const rideId = ride.ride_id || ride.external_reference_id || '';
      if (rideId && !currentRideIds.has(rideId)) {
        // Ride was deleted from layer, remove from context
        removeRide(rideId);
      }
    });
  }, [context?.global?.mapLayers, rides, removeRide]);

  // Get ride ID for a RideSchema
  const getRideId = (ride: RideSchema): string => {
    return ride.ride_id || ride.external_reference_id || '';
  };

  // Handle layer selection change
  const handleLayerChange = (layerName: string | null) => {
    setLocalSelectedLayer(layerName);
    if (onLayerChange) {
      onLayerChange(layerName);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box 
        sx={{ 
          p: 2.5, 
          borderBottom: 2, 
          borderColor: 'secondary.main',
          flexShrink: 0,
          backgroundColor: 'rgba(156, 39, 176, 0.04)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            <Typography 
              variant="h6"
              sx={{
                fontWeight: 700,
                color: 'secondary.main',
                fontSize: '1.1rem',
              }}
            >
              {t('rides.panel.atlasTitle')}
            </Typography>
            {availableLayers.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="layer-select-label">{t('rides.panel.layerLabel')}</InputLabel>
                <Select
                  labelId="layer-select-label"
                  id="layer-select"
                  value={localSelectedLayer || ''}
                  onChange={(e) => handleLayerChange(e.target.value || null)}
                  label={t('rides.panel.layerLabel')}
                >
                  <MenuItem value="">
                    <em>{t('rides.panel.allLayers')}</em>
                  </MenuItem>
                  {availableLayers.map((layerName) => (
                    <MenuItem key={layerName} value={layerName}>
                      {layerName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
          <Tooltip title={t('rides.panel.refreshTooltip')}>
            <IconButton
              size="small"
              onClick={handleRefresh}
              disabled={isRefreshing}
              sx={{
                animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }}
            >
              <Icon size={20} name="arrows-rotate" />
            </IconButton>
          </Tooltip>
        </Box>
        
        {/* Date Range Pickers */}
        {(onStartDateChange || onEndDateChange) && (
          <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Box sx={{ flex: 1, minWidth: '150px', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TextField
                label={t('rides.panel.startDateLabel')}
                type="date"
                value={localStartDate}
                onChange={(e) => setLocalStartDate(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                size="small"
                error={!!dateError}
                fullWidth
              />
              <Tooltip
                title={t('rides.panel.startDateTooltipAtlas')}
                arrow
                placement="top"
              >
                <IconButton size="small" sx={{ ml: -1 }}>
                  <Icon name="circle-info" size={20} />
                </IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ flex: 1, minWidth: '150px', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TextField
                label={t('rides.panel.endDateLabel')}
                type="date"
                value={localEndDate}
                onChange={(e) => setLocalEndDate(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                size="small"
                error={!!dateError}
                fullWidth
              />
              <Tooltip
                title={t('rides.panel.endDateTooltipAtlas')}
                arrow
                placement="top"
              >
                <IconButton size="small" sx={{ ml: -1 }}>
                  <Icon name="circle-info" size={20} />
                </IconButton>
              </Tooltip>
            </Box>
            {dateError && (
              <Typography variant="caption" color="error" sx={{ width: '100%' }}>
                {dateError}
              </Typography>
            )}
          </Box>
        )}
        
        <RideSearchBar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          filteredCount={filteredRidesList.length}
          totalCount={rides.length}
          placeholder={t('rides.panel.searchPlaceholder')}
        />
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mt: 1.5,
            p: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
            borderRadius: 1,
          }}
        >
          <Typography 
            variant="body2" 
            sx={{
              color: 'text.secondary',
              fontWeight: 500,
            }}
          >
            {t('rides.panel.countLabel', { filtered: filteredRidesList.length, total: rides.length })}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              onClick={handleSelectAll}
              disabled={filteredRidesList.length === 0}
              variant="outlined"
              sx={{
                textTransform: 'none',
                fontSize: '0.8rem',
              }}
            >
              {t('common.selectAll')}
            </Button>
            <Button
              size="small"
              onClick={handleDeselectAll}
              disabled={selectedRideIds.size === 0}
              variant="outlined"
              sx={{
                textTransform: 'none',
                fontSize: '0.8rem',
              }}
            >
              {t('common.deselectAll')}
            </Button>
          </Box>
        </Box>
        {selectedRideIds.size > 0 && (
          <Box
            sx={{
              mt: 1.5,
              p: 1,
              backgroundColor: 'secondary.main',
              color: 'secondary.contrastText',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {t('rides.panel.selectedLabel', { count: selectedRideIds.size })}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Rides List */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          minHeight: 0,
          p: 2,
          backgroundColor: 'background.default',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(0, 0, 0, 0.05)',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
            },
          },
        }}
      >
        {filteredRidesList.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              {rides.length === 0
                ? t('rides.panel.emptyAtlas')
                : t('rides.panel.emptyNoMatch')}
            </Typography>
          </Box>
        ) : (
          filteredRidesList.map((ride) => {
            const rideId = getRideId(ride);
            return (
              <RideCard
                key={rideId}
                ride={ride}
                source="atlas"
                selected={selectedRideIds.has(rideId)}
                onSelect={(selected) => handleRideSelect(rideId, selected)}
                expanded={expandedRideId === rideId}
                onExpand={(expanded) => setExpandedRideId(expanded ? rideId : null)}
              />
            );
          })
        )}
      </Box>
    </Box>
  );
};

